import { useState } from "react";
import { Button, Card, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { AuditDto } from "@audit-plan/shared";
import { useAuth } from "../auth/AuthContext";
import { api, download } from "../api/client";
import { PageHead } from "../components/dashboard/Kpi";
import { palette } from "../theme";

export default function ReportsPage() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const [auditId, setAuditId] = useState<string>();
  const audits = useQuery({
    queryKey: ["audits", { status: "closed" }],
    queryFn: async () => {
      const r = await api.get<AuditDto[] | { items: AuditDto[] }>("/audits", { status: "closed" });
      return Array.isArray(r) ? r : r.items;
    },
  });
  const year = dayjs().year();
  const exp: { key: string; title: string; desc: string; run: () => void }[] = [
    { key: "plan", title: t("a_exPlan"), desc: t("a_exPlanD"), run: () => download("/plan/export.xlsx", { year }) },
    { key: "suppliers", title: t("a_exSuppliers"), desc: t("a_exSuppliersD"), run: () => download("/exports/suppliers.xlsx") },
    { key: "audits", title: t("a_exAudits"), desc: t("a_exAuditsD"), run: () => download("/exports/audits.xlsx") },
    { key: "actions", title: t("a_exActions"), desc: t("a_exActionsD"), run: () => download("/exports/actions.xlsx") },
    { key: "ncs", title: t("a_exNcs"), desc: t("a_exNcsD"), run: () => download("/exports/ncs.xlsx") },
    ...(me?.role === "admin" || me?.role === "manager" ? [{ key: "log", title: t("a_exLog"), desc: t("a_exLogD"), run: () => download("/exports/log.xlsx") }] : []),
    ...(me?.role === "admin" ? [{ key: "json", title: t("exportJson"), desc: t("a_exJsonD"), run: () => download("/exports/dataset.json") }] : []),
  ];
  return (
    <div>
      <PageHead title={t("reports")} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16, marginBottom: 20 }}>
        {exp.map((e) => (
          <button key={e.key} type="button" onClick={e.run} style={{ textAlign: "left", font: "inherit", cursor: "pointer", background: palette.card, border: `1px solid ${palette.hair}`, borderRadius: 10, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <b style={{ color: palette.heading }}>{e.title}</b>
              <DownloadOutlined style={{ color: palette.primary }} />
            </div>
            <div style={{ fontSize: 12.5, color: palette.muted, marginTop: 4 }}>{e.desc}</div>
          </button>
        ))}
      </div>
      <Card title={t("auditReport")}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
          <Select
            showSearch
            style={{ flex: "1 1 260px", maxWidth: 380, minWidth: 0 }}
            loading={audits.isLoading}
            placeholder={t("a_pickAudit")}
            value={auditId}
            onChange={setAuditId}
            optionFilterProp="label"
            notFoundContent={t("none")}
            options={(audits.data ?? []).map((a) => ({ value: a.id, label: `${a.code} · ${a.supplierName}${a.scores?.total != null ? ` · ${a.scores.total.toFixed(1)}` : ""}` }))}
          />
          <Button type="primary" icon={<DownloadOutlined />} disabled={!auditId} onClick={() => download(`/audits/${auditId}/report.pdf`)}>
            PDF
          </Button>
        </div>
      </Card>
    </div>
  );
}
